import { useState, useEffect, useCallback } from 'react';
import type { Manager } from 'coco-cashu-core';
import type { CashuMint } from './useMintManager';

export interface HistoryEntry {
  id: string;
  type: 'mint' | 'melt' | 'send' | 'receive';
  amount: number;
  mintUrl: string;
  status: 'pending' | 'completed' | 'failed';
  timestamp: number;
  quoteId?: string;
  invoice?: string;
}

interface UseHistoryManagerProps {
  coco: Manager | null;
  repositories: unknown;
  mints: CashuMint[];
}

export function useHistoryManager({
  coco,
  repositories,
  mints,
}: UseHistoryManagerProps) {
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const refreshHistory = useCallback(async () => {
    if (!coco || !repositories) return;

    setIsLoading(true);
    try {
      const allHistory: HistoryEntry[] = [];

      // The coco-cashu repository API has shifted method names across versions
      // (e.g. `getAll`, `getAllQuotes`, `list`). Probe for whichever exists so a
      // version bump can't crash the whole history view.
      const listFrom = async (repo: unknown): Promise<Array<Record<string, unknown>>> => {
        if (!repo || typeof repo !== 'object') return [];
        const r = repo as Record<string, unknown>;
        const candidates = ['getAll', 'getAllQuotes', 'list', 'getQuotes', 'all'];
        for (const name of candidates) {
          const fn = r[name];
          if (typeof fn === 'function') {
            try {
              const res = await (fn as () => Promise<unknown>).call(repo);
              if (Array.isArray(res)) return res as Array<Record<string, unknown>>;
            } catch {
              // try the next candidate
            }
          }
        }
        return [];
      };

      const repos = repositories as Record<string, unknown>;

      const toEntry = (
        quote: Record<string, unknown>,
        type: 'mint' | 'melt',
      ): HistoryEntry => {
        const state = quote.state as string | undefined;
        return {
          id: String(quote.quoteId ?? quote.quote ?? quote.id ?? ''),
          type,
          amount: Number(quote.amount ?? 0),
          mintUrl: String(quote.mintUrl ?? ''),
          status: state === 'paid' ? 'completed' : state === 'pending' ? 'pending' : 'failed',
          timestamp: Number(quote.createdAt ?? Date.now()),
          quoteId: quote.quoteId ? String(quote.quoteId) : undefined,
          invoice: quote.request ? String(quote.request) : undefined,
        };
      };

      // Fetch mint quotes (receive operations)
      const mintQuotes = await listFrom(repos.mintQuoteRepository);
      for (const quote of mintQuotes) allHistory.push(toEntry(quote, 'mint'));

      // Fetch melt quotes (send operations)
      const meltQuotes = await listFrom(repos.meltQuoteRepository);
      for (const quote of meltQuotes) allHistory.push(toEntry(quote, 'melt'));

      // Sort by timestamp (newest first)
      allHistory.sort((a, b) => b.timestamp - a.timestamp);

      setHistory(allHistory);
    } catch (err) {
      console.error('[Cashu] Failed to refresh history:', err);
    } finally {
      setIsLoading(false);
    }
  }, [coco, repositories]);

  // Refresh history when coco initializes
  useEffect(() => {
    if (coco) {
      refreshHistory();
    }
  }, [coco, refreshHistory]);

  // Listen to events for real-time updates
  useEffect(() => {
    if (!coco) return;

    const unsubs = [
      coco.on('mint-quote:created', refreshHistory),
      coco.on('mint-quote:redeemed', refreshHistory),
      coco.on('melt-quote:created', refreshHistory),
      coco.on('melt-quote:paid', refreshHistory),
      coco.on('send:created', refreshHistory),
      coco.on('receive:created', refreshHistory),
    ];

    return () => unsubs.forEach(unsub => unsub());
  }, [coco, refreshHistory]);

  return {
    history,
    isLoading,
    refreshHistory,
  };
}
